/* Foro de abogados, visto desde el panel.
 *
 * Dos cosas que conviene tener presentes al tocar este archivo:
 *
 *   1. Todo lo que se escribe acá es PÚBLICO y va firmado con nombre y
 *      apellido, salvo que se marque anónimo. El panel es privado y el foro no,
 *      y la pantalla tiene que dejarlo obvio en todo momento — el error caro no
 *      es un fallo del programa, es alguien contando un caso creyendo que
 *      escribe adentro del estudio.
 *
 *   2. El foro es solo texto. No hay adjuntos ni pegar imágenes, y no es que
 *      falten: no van a existir.
 *
 * Quién puede publicar lo decide el servidor; acá solo se pregunta para saber
 * si se dibuja el botón.
 */
import React, { useEffect, useState, useCallback } from 'react';
import * as FS from '../../utils/datos';

const fechaCorta = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia
    ? `hoy ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    : FS.fmtDate(iso);
};

/** La advertencia va donde se escribe, no en unos términos que nadie abre.
 *  Es la única defensa real contra contar un caso con nombre y apellido. */
const AvisoSecreto: React.FC = () => (
  <div style={{
    display: 'flex', gap: 10, alignItems: 'flex-start',
    background: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.25)',
    borderRadius: 8, padding: '10px 13px', marginBottom: 12,
  }}>
    <span style={{ fontSize: 15, lineHeight: 1.3 }}>⚠️</span>
    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
      Esto lo puede leer <strong style={{ color: 'var(--white)' }}>cualquiera</strong>, tenga cuenta o no,
      y aparece en buscadores. Pregunta por el criterio, no por el caso: sin nombres de clientes, sin RUT,
      sin causas identificables. El foro es solo texto — no se pueden adjuntar documentos, y es a propósito.
    </div>
  </div>
);

const Firma: React.FC<{ p: FS.ForoFirma }> = ({ p }) => (
  <span style={{ fontSize: 12, color: p.anonimo ? 'var(--text-muted)' : '#64B5F6' }}>
    {p.autor}
  </span>
);

const CasillaAnonimo: React.FC<{ valor: boolean; onChange: (v: boolean) => void }> = ({ valor, onChange }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
    <input type="checkbox" checked={valor} onChange={e => onChange(e.target.checked)} />
    Publicar como <strong style={{ color: 'var(--white)' }}>abogado verificado</strong>, sin mi nombre
  </label>
);

/* ── Un tema abierto, con sus respuestas ────────────────────────────────── */
const TemaDetalle: React.FC<{ slug: string; puedePublicar: boolean; onVolver: () => void }> = ({ slug, puedePublicar, onVolver }) => {
  const [datos, setDatos] = useState<{ tema: any; respuestas: FS.ForoMensaje[] } | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [respuesta, setRespuesta] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [denunciando, setDenunciando] = useState<{ temaId?: string; respuestaId?: string } | null>(null);
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    FS.foroTema(slug)
      .then(d => { setDatos(d); setError(''); })
      .catch(() => setError('No pudimos abrir este tema. Puede haber sido retirado.'))
      .finally(() => setCargando(false));
  }, [slug]);
  useEffect(cargar, [cargar]);

  const enviar = async () => {
    if (!datos) return;
    if (respuesta.trim().length < 2) { setError('Escribe tu respuesta antes de publicarla.'); return; }
    setEnviando(true);
    setError('');
    try {
      await FS.foroResponder(datos.tema.id, { cuerpo: respuesta, anonimo });
      setRespuesta(''); setAnonimo(false); cargar();
    } catch (e: any) {
      setError(e?.message || 'No pudimos publicar tu respuesta. Inténtalo de nuevo.');
    } finally { setEnviando(false); }
  };

  const enviarDenuncia = async () => {
    if (!denunciando || motivo.trim().length < 5) return;
    try {
      await FS.foroDenunciar({ ...denunciando, motivo });
      setDenunciando(null); setMotivo('');
      alert('Gracias. La revisamos y te avisamos si corresponde.');
    } catch { alert('No pudimos registrar la denuncia. Inténtalo de nuevo.'); }
  };

  if (cargando) return <div className="p-section"><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</div></div>;
  if (error && !datos) return (
    <div className="p-section">
      <button className="p-btn-secondary" onClick={onVolver} style={{ marginBottom: 14 }}>← Volver al foro</button>
      <div style={{ color: '#e57373', fontSize: 13 }}>{error}</div>
    </div>
  );
  if (!datos) return null;

  const { tema, respuestas } = datos;
  const botonDenuncia = (d: { temaId?: string; respuestaId?: string }) => (
    <button
      onClick={() => { setDenunciando(d); setMotivo(''); }}
      title="Denunciar esta publicación"
      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer', opacity: 0.6, padding: '0 2px' }}
    >⚑ Denunciar</button>
  );

  return (
    <div className="p-section">
      <button className="p-btn-secondary" onClick={onVolver} style={{ marginBottom: 16 }}>← Volver al foro</button>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
        {tema.categoria?.nombre}
      </div>
      <h2 style={{ fontSize: 21, color: 'var(--white)', margin: '0 0 10px', lineHeight: 1.3, fontWeight: 600 }}>{tema.titulo}</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <Firma p={tema} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fechaCorta(tema.creado)}</span>
        {puedePublicar && botonDenuncia({ temaId: tema.id })}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.75, color: '#DCE5F5', whiteSpace: 'pre-wrap', paddingBottom: 22, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {tema.cuerpo}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '18px 0 4px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {respuestas.length === 0 ? 'Sin respuestas todavía' : `${respuestas.length} respuesta${respuestas.length === 1 ? '' : 's'}`}
      </div>

      {respuestas.map(r => (
        <div key={r.id} style={{ padding: '15px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 7 }}>
            <Firma p={r} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fechaCorta(r.creado)}</span>
            {puedePublicar && botonDenuncia({ respuestaId: r.id })}
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.7, color: '#CBD7EC', whiteSpace: 'pre-wrap' }}>{r.cuerpo}</div>
        </div>
      ))}

      {puedePublicar ? (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 10 }}>Responder</div>
          <AvisoSecreto />
          <textarea
            className="p-input"
            value={respuesta}
            onChange={e => setRespuesta(e.target.value)}
            placeholder="Cuenta tu experiencia con esto…"
            rows={5}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
          />
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <CasillaAnonimo valor={anonimo} onChange={setAnonimo} />
            <button className="p-btn-primary" onClick={enviar} disabled={enviando} style={{ marginLeft: 'auto' }}>
              {enviando ? 'Publicando…' : 'Publicar respuesta'}
            </button>
          </div>
          {error && <div style={{ color: '#e57373', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </div>
      ) : (
        <div style={{ marginTop: 22, fontSize: 12.5, color: 'var(--text-muted)', padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
          En el foro publican los abogados del estudio. Puedes leer todo lo que quieras.
        </div>
      )}

      {denunciando && (
        <div className="panel-modal-fondo" onClick={() => setDenunciando(null)}>
          <div className="panel-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, color: '#E8EEF9', marginBottom: 6, fontWeight: 500 }}>Denunciar publicación</div>
            <div style={{ fontSize: 12.5, color: '#9BB0CC', marginBottom: 14, lineHeight: 1.6 }}>
              Cuéntanos qué problema tiene. La revisa el equipo de Tabularis; el autor no ve quién denunció.
            </div>
            <textarea
              className="p-input" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
              placeholder="Ej: identifica a un cliente por su nombre"
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="p-btn-secondary" onClick={() => setDenunciando(null)}>Cancelar</button>
              <button className="p-btn-primary" onClick={enviarDenuncia} disabled={motivo.trim().length < 5}>Enviar denuncia</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Listado ────────────────────────────────────────────────────────────── */
/** `slugInicial` llega cuando alguien venía leyendo un hilo en el foro público
 *  y pinchó "responder": entró a su panel y hay que dejarlo en ESE hilo, no en
 *  el listado. Volver al listado desde ahí funciona como siempre. */
const Foro: React.FC<{ slugInicial?: string; onSlugUsado?: () => void }> = ({ slugInicial, onSlugUsado }) => {
  const [categorias, setCategorias] = useState<FS.ForoCategoria[]>([]);
  const [categoria, setCategoria] = useState<string>('');
  const [temas, setTemas] = useState<FS.ForoTema[]>([]);
  const [cargando, setCargando] = useState(true);
  const [puedePublicar, setPuedePublicar] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(slugInicial ?? null);
  const [escribiendo, setEscribiendo] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [catNueva, setCatNueva] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    FS.foroCategorias().then(setCategorias).catch(() => {});
    FS.foroPermiso().then(setPuedePublicar);
    // El hilo de llegada se usa UNA vez. `abierto` ya lo tomó como valor
    // inicial, así que soltarlo acá no lo cierra; sin esto, cada visita al foro
    // desde el menú volvería a abrir el mismo hilo para siempre.
    if (slugInicial) onSlugUsado?.();
  }, []);

  // Si el formulario se abrió antes de que llegaran las categorías, la selección
  // quedaría vacía y el tema no se podría publicar. Se rellena en cuanto están.
  useEffect(() => {
    if (!catNueva && categorias.length) setCatNueva(categorias[0].slug);
  }, [categorias, catNueva]);

  const cargarTemas = useCallback(() => {
    setCargando(true);
    FS.foroTemas(categoria || undefined)
      .then(r => setTemas(r.temas))
      .catch(() => setTemas([]))
      .finally(() => setCargando(false));
  }, [categoria]);
  useEffect(cargarTemas, [cargarTemas]);

  /** Qué le falta al formulario, dicho en palabras. Devuelve null si está listo.
   *
   *  Existe porque el botón antes se deshabilitaba en silencio: con un mensaje
   *  corto quedaba gris y pulsarlo no hacía nada ni decía por qué. Quien
   *  escribe no tiene forma de adivinar que faltan caracteres. */
  const queFalta = (): string | null => {
    if (titulo.trim().length < 8) return `El título necesita al menos 8 caracteres; llevas ${titulo.trim().length}.`;
    if (cuerpo.trim().length < 20) return `El mensaje necesita al menos 20 caracteres; llevas ${cuerpo.trim().length}.`;
    if (!catNueva) return 'Elige una categoría para el tema.';
    return null;
  };

  const publicar = async () => {
    // Nunca se sale en silencio: si algo falta, se dice.
    const falta = queFalta();
    if (falta) { setError(falta); return; }
    setEnviando(true);
    setError('');
    try {
      const { slug } = await FS.foroCrearTema({ titulo, cuerpo, categoria: catNueva, anonimo });
      setEscribiendo(false); setTitulo(''); setCuerpo(''); setAnonimo(false);
      setAbierto(slug);
    } catch (e: any) {
      setError(e?.message || 'No pudimos publicar el tema. Inténtalo de nuevo.');
    } finally { setEnviando(false); }
  };

  if (abierto) return <TemaDetalle slug={abierto} puedePublicar={puedePublicar} onVolver={() => { setAbierto(null); cargarTemas(); }} />;

  return (
    <div className="p-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
        <div>
          <div className="p-section-title" style={{ marginBottom: 4 }}>💬 Foro de abogados</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 620, lineHeight: 1.6 }}>
            Preguntas entre colegas: cómo se comporta un tribunal, cómo se llevó un caso parecido, quién sabe de una materia.
            Se lee desde fuera y aparece en buscadores — <strong style={{ color: 'var(--white)' }}>no es el panel de tu estudio</strong>.
          </div>
        </div>
        {puedePublicar && !escribiendo && (
          <button className="p-btn-primary" onClick={() => { setEscribiendo(true); setCatNueva(categoria || categorias[0]?.slug || ''); }}>
            ＋ Nuevo tema
          </button>
        )}
      </div>

      {escribiendo && (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 18, margin: '18px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 12 }}>Nuevo tema</div>
          <AvisoSecreto />
          <input
            className="p-input" value={titulo} onChange={e => setTitulo(e.target.value)}
            placeholder="Título — la pregunta en una línea" maxLength={140}
            style={{ marginBottom: 10 }}
          />
          <select className="p-select" value={catNueva} onChange={e => setCatNueva(e.target.value)} style={{ marginBottom: 10 }}>
            {categorias.map(c => <option key={c.slug} value={c.slug}>{c.nombre}</option>)}
          </select>
          <textarea
            className="p-input" value={cuerpo} onChange={e => setCuerpo(e.target.value)} rows={7}
            placeholder="Cuenta el contexto y qué necesitas saber. Sin datos que permitan identificar a un cliente."
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
          />
          {queFalta() && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8 }}>{queFalta()}</div>
          )}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <CasillaAnonimo valor={anonimo} onChange={setAnonimo} />
            <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
              <button className="p-btn-secondary" onClick={() => { setEscribiendo(false); setError(''); }}>Cancelar</button>
              <button className="p-btn-primary" onClick={publicar} disabled={enviando}>
                {enviando ? 'Publicando…' : 'Publicar tema'}
              </button>
            </div>
          </div>
          {error && <div style={{ color: '#e57373', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '18px 0 14px' }}>
        <button className={categoria === '' ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setCategoria('')} style={{ padding: '5px 12px', fontSize: 12 }}>Todo</button>
        {categorias.map(c => (
          <button key={c.slug} className={categoria === c.slug ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setCategoria(c.slug)} style={{ padding: '5px 12px', fontSize: 12 }} title={c.descripcion}>
            {c.nombre}
          </button>
        ))}
      </div>

      {cargando && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>Cargando…</div>}

      {!cargando && temas.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '28px 0', lineHeight: 1.7 }}>
          Todavía no hay temas en esta categoría.
          {puedePublicar && <> Si tienes una duda a mano, escríbela: el foro parte cuando alguien pregunta primero.</>}
        </div>
      )}

      {!cargando && temas.map(t => (
        <button
          key={t.slug}
          onClick={() => setAbierto(t.slug)}
          style={{
            display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '14px 4px', cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, color: 'var(--white)', fontWeight: 500, lineHeight: 1.4 }}>{t.titulo}</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {t.respuestas === 0 ? 'sin respuestas' : `${t.respuestas} respuesta${t.respuestas === 1 ? '' : 's'}`}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 5 }}>
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>{t.categoria?.nombre}</span>
            <Firma p={t} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {fechaCorta(t.ultimaActividad)}</span>
          </div>
        </button>
      ))}
    </div>
  );
};

export default Foro;
